import { useState } from "react";
import { addDays, compareIsoDate, todayIn, weekday, type IsoDate } from "../../../shared/dates";
import type { Member, RewardCurrency, Task, TaskInput, TaskKind, TaskRecurrence } from "../../../shared/models";
import { kidsFirst } from "../../../shared/people";
import { describeRecurrence } from "../../../shared/tasks";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useMembers, useRewards, useTaskActions, useTasks } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { Confirm, Sheet } from "../../ui/Sheet";
import { Slider } from "../../ui/Slider";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { formatReward, parseReward } from "../../ui/reward";
import { useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { fmtDate } from "../calendar/calendarModel";
import { WidgetHeader } from "../WidgetHeader";

interface TasksConfig {
  title: string;
  showCompleted: boolean;
  /** "todo": one-off and recurring to-dos. "reminders": only recurring household tasks, soonest first. */
  show: "todo" | "reminders";
}

export function dueLabel(due: IsoDate | null, today: IsoDate): { text: string; tone: "overdue" | "today" | "soon" | "" } {
  if (!due) return { text: "", tone: "" };
  if (compareIsoDate(due, today) < 0) return { text: due === addDays(today, -1) ? "Yesterday" : `Due ${fmtDate(due, { month: "short", day: "numeric" })}`, tone: "overdue" };
  if (due === today) return { text: "Today", tone: "today" };
  if (due === addDays(today, 1)) return { text: "Tomorrow", tone: "soon" };
  if (compareIsoDate(due, addDays(today, 6)) <= 0) return { text: fmtDate(due, { weekday: "long" }), tone: "" };
  return { text: fmtDate(due, { month: "short", day: "numeric" }), tone: "" };
}

function Tasks({ config, size, editing, openFullscreen, fullscreen }: WidgetProps<TasksConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const q = useTasks();
  const members = useMembers().data ?? [];
  const actions = useTaskActions();
  const { toast } = useAppUi();
  const state = dataStateOf(q, 20 * 60_000);
  const [filter, setFilter] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | "new" | null>(null);

  // Chores live on the chore chart; this widget lists household to-dos and reminders.
  const tasks = (q.data ?? []).filter((t) => t.kind !== "chore" && (!filter || t.assigneeId === filter) && (config.show !== "reminders" || t.recurrence));
  const open = tasks.filter((t) => !t.completedAt).sort((a, b) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || a.createdAt.localeCompare(b.createdAt));
  const done = config.showCompleted ? tasks.filter((t) => t.completedAt) : [];
  const memberById = new Map(members.map((m) => [m.id, m]));

  const complete = (t: Task) => {
    actions.complete.mutate({ task: t }, {
      onSuccess: (updated) =>
        toast(t.recurrence ? `“${t.title}” done · next ${dueLabel(updated.dueDate, today).text || "scheduled"}` : `“${t.title}” done`, {
          label: "Undo",
          run: () => actions.reopen.mutate(updated),
        }),
      onError: (e) => toast(`Couldn’t complete “${t.title}”: ${(e as Error).message}`),
    });
  };

  return (
    <div className="tasks">
      <WidgetHeader
        title={config.title || (config.show === "reminders" ? "Reminders" : "Tasks")}
        meta={open.length ? `${open.length} to do` : undefined}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <button className="icon-btn icon-btn-accent" onClick={() => setEditingTask("new")} aria-label="Add task" disabled={editing}>
            <Icon name="plus" />
          </button>
        }
      />
      {members.length > 1 && (size.height > 440 || fullscreen) && (
        <div className="chip-row tasks-filter" role="tablist" aria-label="Filter by person">
          <button className={`chip chip-sm ${filter === null ? "on" : ""}`} onClick={() => setFilter(null)}>
            Everyone
          </button>
          {members.map((m) => (
            <button key={m.id} className={`chip chip-sm ${filter === m.id ? "on" : ""}`} style={{ ["--member" as string]: m.color }} onClick={() => setFilter(m.id)}>
              <i className="dot" />
              {m.displayName}
            </button>
          ))}
        </div>
      )}

      {!q.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="tasks" title="Tasks unavailable" detail={q.error?.message} />
      ) : open.length === 0 && done.length === 0 ? (
        <EmptyState icon="check" title="All clear" detail="Nothing on the list." />
      ) : (
        <ul className="task-list">
          {open.map((t) => {
            const due = dueLabel(t.dueDate, today);
            const who = t.assigneeId ? memberById.get(t.assigneeId) : undefined;
            return (
              <li key={t.id} className="task">
                <button className="task-check" onClick={() => complete(t)} aria-label={`Complete ${t.title}`} disabled={editing}>
                  <span className="box" />
                </button>
                <button className="task-body" onClick={() => setEditingTask(t)} disabled={editing}>
                  <span className="task-title">{t.title}</span>
                  <span className="task-meta">
                    {due.text && <span className={`due due-${due.tone}`}>{due.text}</span>}
                    {t.recurrence && (
                      <span className="recur">
                        <Icon name="refresh" size="0.8rem" />
                        {describeRecurrence(t.recurrence)}
                      </span>
                    )}
                  </span>
                </button>
                {who && <Avatar member={who} size="1.9rem" />}
              </li>
            );
          })}
          {done.map((t) => (
            <li key={t.id} className="task done">
              <button className="task-check checked" onClick={() => actions.reopen.mutate(t)} aria-label={`Reopen ${t.title}`} disabled={editing}>
                <span className="box">
                  <Icon name="check" size="1rem" />
                </span>
              </button>
              <span className="task-body">
                <span className="task-title">{t.title}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {editingTask && (
        <TaskEditor
          task={editingTask === "new" ? null : editingTask}
          kind="task"
          defaultRepeat={config.show === "reminders" ? "monthly" : "none"}
          members={members}
          today={today}
          onClose={() => setEditingTask(null)}
        />
      )}
    </div>
  );
}

export type RepeatKind = "none" | "daily" | "weekly" | "monthly";

/** Editor for to-dos, reminders and chores (chores add a reward and default to daily). */
export function TaskEditor({
  task,
  kind,
  defaultRepeat = "none",
  defaultAssignee = null,
  members,
  today,
  onClose,
}: {
  task: Task | null;
  kind: TaskKind;
  defaultRepeat?: RepeatKind;
  defaultAssignee?: string | null;
  members: Member[];
  today: IsoDate;
  onClose: () => void;
}) {
  const actions = useTaskActions();
  const currency: RewardCurrency = useRewards().data?.currency ?? "stars";
  const isChore = (task?.kind ?? kind) === "chore";
  const [title, setTitle] = useState(task?.title ?? "");
  const [due, setDue] = useState<IsoDate | null>(task?.dueDate ?? null);
  const [assignee, setAssignee] = useState<string | null>(task?.assigneeId ?? defaultAssignee);
  const [repeat, setRepeat] = useState<RepeatKind>(task ? (task.recurrence?.kind ?? "none") : defaultRepeat);
  const [reward, setReward] = useState(task ? task.reward : currency === "dollars" ? 100 : 1);
  const [weekdays, setWeekdays] = useState<number[]>(task?.recurrence?.kind === "weekly" ? task.recurrence.weekdays : [weekday(due ?? today)]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const busy = actions.create.isPending || actions.update.isPending;

  const recurrence: TaskRecurrence | null =
    repeat === "none" ? null : repeat === "weekly" ? { kind: "weekly", interval: 1, weekdays } : { kind: repeat, interval: 1 };

  const save = () => {
    if (!title.trim()) {
      setError("Give the task a name.");
      return;
    }
    const input: TaskInput = { title: title.trim(), dueDate: due, assigneeId: assignee, recurrence, kind: isChore ? "chore" : "task", reward: isChore ? reward : 0 };
    const opts = { onSuccess: onClose, onError: (e: Error) => setError(e.message) };
    if (task) actions.update.mutate({ id: task.id, patch: input }, opts);
    else actions.create.mutate(input, opts);
  };

  const quickDates: { label: string; value: IsoDate | null }[] = [
    { label: "No date", value: null },
    { label: "Today", value: today },
    { label: "Tomorrow", value: addDays(today, 1) },
    { label: "Next week", value: addDays(today, 7) },
  ];

  return (
    <Sheet
      title={task ? (isChore ? "Edit chore" : "Edit task") : isChore ? "New chore" : "New task"}
      onClose={onClose}
      footer={
        <>
          {task && (
            <button className="btn btn-danger-ghost" onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" size="1.1rem" /> Delete
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="form">
        <label className="field">
          <span>{isChore ? "Chore" : "Task"}</span>
          <input
            className="input input-lg"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isChore ? "e.g. Feed the dog" : "e.g. Change the furnace filter"}
            maxLength={140}
            autoFocus
          />
        </label>
        {isChore && <RewardStepper value={reward} currency={currency} onChange={setReward} />}
        <div className="field">
          <span>Due</span>
          <div className="chip-row wrap">
            {quickDates.map((d) => (
              <button key={d.label} className={`chip ${due === d.value ? "on" : ""}`} onClick={() => setDue(d.value)}>
                {d.label}
              </button>
            ))}
            <input className="input input-date" type="date" value={due ?? ""} onChange={(e) => setDue(e.target.value || null)} aria-label="Pick a date" />
          </div>
        </div>
        {members.length > 0 && (
          <div className="field">
            <span>{isChore ? "Whose chore?" : "For"}</span>
            <div className="chip-row wrap">
              <button className={`chip ${assignee === null ? "on" : ""}`} onClick={() => setAssignee(null)}>
                Anyone
              </button>
              {(isChore ? kidsFirst(members) : members).map((m) => (
                <button key={m.id} className={`chip ${assignee === m.id ? "on" : ""}`} style={{ ["--member" as string]: m.color }} onClick={() => setAssignee(m.id)}>
                  <i className="dot" />
                  {m.displayName}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="field">
          <span>Repeat</span>
          <div className="segmented">
            {(["none", "daily", "weekly", "monthly"] as RepeatKind[]).map((r) => (
              <button key={r} className={repeat === r ? "on" : ""} onClick={() => setRepeat(r)}>
                {r === "none" ? "Never" : r[0]!.toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>
          {repeat === "weekly" && (
            <div className="chip-row weekdays">
              {["S", "M", "T", "W", "T", "F", "S"].map((l, i) => (
                <button
                  key={i}
                  className={`chip round ${weekdays.includes(i) ? "on" : ""}`}
                  onClick={() => setWeekdays((w) => (w.includes(i) ? w.filter((x) => x !== i) : [...w, i].sort()))}
                  aria-label={["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][i]}
                  aria-pressed={weekdays.includes(i)}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
          {repeat !== "none" && !due && <p className="muted small">Starts today.</p>}
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
      {confirmDelete && task && (
        <Confirm
          title={isChore ? "Delete chore?" : "Delete task?"}
          message={`“${task.title}” will be removed for everyone.`}
          confirmLabel="Delete"
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => actions.remove.mutate(task, { onSuccess: onClose, onError: (e) => setError(e.message) })}
        />
      )}
    </Sheet>
  );
}

/** Dollars slide from $0 to $20 in quarters (further if a reward is already bigger). */
const DOLLARS_MAX = 2000;
const DOLLARS_STEP = 25;

/** A chore's reward: a slider for dollars, big +/− for stars. */
export function RewardStepper({ value, currency, onChange }: { value: number; currency: RewardCurrency; onChange: (v: number) => void }) {
  const [text, setText] = useState<string | null>(null);
  if (currency === "dollars") {
    const step = DOLLARS_STEP;
    const max = Math.max(DOLLARS_MAX, Math.ceil(value / 500) * 500);
    return (
      <div className="field">
        <span>Reward</span>
        <div className="reward-slider">
          <button className="icon-btn icon-btn-accent" onClick={() => onChange(Math.max(0, value - step))} aria-label="Less">
            <span className="minus" aria-hidden="true">
              −
            </span>
          </button>
          <div className="reward-slider-body">
            <strong className="reward-slider-value" aria-hidden="true">
              {(value / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </strong>
            <Slider value={value} min={0} max={max} step={step} onChange={onChange} label="Reward amount" valueText={formatReward(value, currency)} />
            <span className="reward-slider-scale" aria-hidden="true">
              <span>$0</span>
              <span>{formatReward(max / 2, currency)}</span>
              <span>{formatReward(max, currency)}</span>
            </span>
          </div>
          <button className="icon-btn icon-btn-accent" onClick={() => onChange(Math.min(max, value + step))} aria-label="More">
            <Icon name="plus" />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="field">
      <span>Reward</span>
      <div className="stepper">
        <button className="icon-btn icon-btn-accent" onClick={() => onChange(Math.max(0, value - 1))} aria-label="Less">
          <span className="minus" aria-hidden="true">
            −
          </span>
        </button>
        <input
          className="input stepper-value"
          inputMode="numeric"
          value={text ?? formatReward(value, currency)}
          onFocus={() => setText(String(value))}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const v = parseReward(text ?? "", currency);
            if (v !== null) onChange(v);
            setText(null);
          }}
          aria-label="Reward amount"
        />
        <button className="icon-btn icon-btn-accent" onClick={() => onChange(value + 1)} aria-label="More">
          <Icon name="plus" />
        </button>
      </div>
    </div>
  );
}

export const tasksWidget: WidgetDefinition<TasksConfig> = {
  type: "tasks",
  version: 1,
  name: "Tasks & reminders",
  description: "Shared to-dos, or recurring household reminders (filters, bills, trash day).",
  icon: "tasks",
  category: "household",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 360,
  defaults: { title: "", showCompleted: true, show: "todo" },
  settings: [
    {
      key: "show",
      label: "Show",
      type: "select",
      options: [
        { value: "todo", label: "To-dos" },
        { value: "reminders", label: "Household reminders" },
      ],
    },
    { key: "title", label: "Title (optional)", type: "text", maxLength: 30 },
    { key: "showCompleted", label: "Show recently completed", type: "toggle" },
  ],
  provider: "kinmantle-tasks",
  supportsFullscreen: true,
  Component: Tasks,
};
